import React, { useState, useEffect } from "react";
import { Card, Row, Col, Table, Tag, Radio, Select, Space, Button, Modal, Form, Input, message } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, ArrowUpOutlined, ArrowDownOutlined, SortAscendingOutlined, FilePdfOutlined, FileImageOutlined, FileTextOutlined, FileOutlined } from "@ant-design/icons";
import axios from "axios";
import WorkflowDiagram from "../../purchase-template/components/WorkflowDiagram";
import { SourcingFormData } from "../interface";

interface SourcingReviewStepProps {
  formData: SourcingFormData;
  onApprovalChange: (approvalData: { approvalType: 'LOCAL' | 'EXTERNAL'; steps?: any[]; externalWorkflow?: any }) => void;
}

export const SourcingReviewStep: React.FC<SourcingReviewStepProps> = ({
  formData,
  onApprovalChange,
}) => {
  const [stepForm] = Form.useForm();
  const [isStepModalVisible, setIsStepModalVisible] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const [approvalType, setApprovalType] = useState<'LOCAL' | 'EXTERNAL'>(formData.approvalType || 'EXTERNAL');
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(formData.externalWorkflow || null);
  const [steps, setSteps] = useState<any[]>(formData.steps || []);

  const getMimeTypeIcon = (mimeType: string) => {
    if (mimeType.includes("pdf")) {
      return <FilePdfOutlined />;
    } else if (mimeType.includes("image")) {
      return <FileImageOutlined />;
    } else if (mimeType.includes("text")) {
      return <FileTextOutlined />;
    }
    return <FileOutlined />;
  };

  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const getCategoryLabel = (category: string) => {
    const categories: { [key: string]: string } = {
      'TOR': 'Terms of Reference',
      'SPECIFICATION': 'Technical Specification',
      'CONTRACT': 'Contract Template',
      'EVALUATION': 'Evaluation Criteria',
      'QUOTE': 'Quotation Document',
      'OTHER': 'Other',
    };
    return categories[category] || category;
  };


  const fetchWorkflows = async () => {
    try {
      const res = await axios.get(`${(import.meta as any).env.VITE_USE_FLOW_API_URL}/workflows`, { withCredentials: true });
      setWorkflows(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    // Sync local state with formData prop when it changes after initial load
    if (formData.approvalType) {
      setApprovalType(formData.approvalType);
    }
    if (formData.steps && formData.steps.length > 0) {
      setSteps(formData.steps);
    }
    if (formData.externalWorkflow) {
      setSelectedWorkflow(formData.externalWorkflow);
    }
  }, [formData.approvalType, formData.steps, formData.externalWorkflow]);

  useEffect(() => {
    if (approvalType === 'EXTERNAL') {
      fetchWorkflows();
    }
  }, [approvalType]);

  useEffect(() => {
    onApprovalChange({
      approvalType,
      steps: approvalType === 'LOCAL' ? steps : undefined,
      externalWorkflow: approvalType === 'EXTERNAL' ? selectedWorkflow : undefined,
    });
  }, [approvalType, steps, selectedWorkflow, onApprovalChange]);

  const addStep = () => {
    stepForm.resetFields();
    setEditingStepIndex(null);
    setIsStepModalVisible(true);
  };

  const editStep = (index: number) => {
    const step = steps[index];
    stepForm.setFieldsValue({
      roleSlug: step.roleSlug,
      stepName: step.stepName,
    });
    setEditingStepIndex(index);
    setIsStepModalVisible(true);
  };

  const saveStep = () => {
    stepForm.validateFields()
      .then(values => {
        let step: any;
        if (editingStepIndex !== null) {
          step = {
            order: steps[editingStepIndex].order,
            roleSlug: values.roleSlug,
            stepName: values.stepName,
          };
          const newSteps = [...steps];
          newSteps[editingStepIndex] = step;
          setSteps(newSteps);
          setEditingStepIndex(null);
        } else {
          const newOrder = (steps.length > 0 ? Math.max(...steps.map(s => s.order)) : 0) + 1;
          step = {
            order: newOrder,
            roleSlug: values.roleSlug,
            stepName: values.stepName,
          };
          setSteps([...steps, step]);
        }
        stepForm.resetFields();
        setIsStepModalVisible(false);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const removeStep = (index: number) => {
    const newSteps = [...steps];
    newSteps.splice(index, 1);
    setSteps(newSteps);
  };

  const moveStep = (index: number, dir: -1 | 1) => {
    if (!steps.length) return;
    const target = index + dir;
    if (target < 0 || target >= steps.length) return;
    const newSteps = [...steps];
    const [a, b] = [newSteps[index], newSteps[target]];
    const tmp = a.order;
    a.order = b.order;
    b.order = tmp;
    newSteps[index] = b;
    newSteps[target] = a;
    setSteps(newSteps);
  };

  const sortByOrder = () => {
    if (!steps?.length) return;
    const newSteps = [...steps].sort((x, y) => Number(x.order ?? 0) - Number(y.order ?? 0));
    setSteps(newSteps);
    message.success("Steps sorted by order");
  };

  const columns = [
    {
      title: "Order",
      dataIndex: "order",
      width: 90,
    },
    {
      title: "Role Slug",
      dataIndex: "roleSlug",
    },
    {
      title: "Step Name",
      dataIndex: "stepName",
    },
    {
      title: "Actions",
      width: 160,
      render: (_: any, _r: any, index: number) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} onClick={() => moveStep(index, -1)} />
          <Button size="small" icon={<ArrowDownOutlined />} onClick={() => moveStep(index, +1)} />
          <Button size="small" icon={<EditOutlined />} onClick={() => editStep(index)} />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeStep(index)} />
        </Space>
      ),
    },
  ];

  const itemColumns = [
    {
      title: 'Item Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (price: number) => `${formData.currency || 'ETB'} ${price.toLocaleString()}`
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (price: number) => `${formData.currency || 'ETB'} ${price.toLocaleString()}`
    },
  ];

  return (
    <div>
      {/* Sourcing Summary */}
      <Card title="Sourcing Summary" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={24}>
            <strong>Title:</strong> {formData.title || 'No title provided'}
          </Col>
          <Col span={24} style={{ marginTop: 8 }}>
            <strong>Description:</strong> {formData.description || 'No description provided'}
          </Col>
          <Col span={12}>
            <strong>Type:</strong> <Tag color="blue">{formData.type}</Tag>
          </Col>
          <Col span={12}>
            <strong>Scope:</strong> <Tag color="green">{formData.sourcingScope}</Tag>
          </Col>
          <Col span={12}>
            <strong>Invitation Mode:</strong> <Tag color="orange">{formData.invitationMode}</Tag>
          </Col>
          <Col span={12}>
            <strong>Total Amount:</strong> {formData.currency || 'ETB'} {formData.totalAmount?.toLocaleString()}
          </Col>
        </Row>
        {formData.notes && (
          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <strong>Notes:</strong> {formData.notes}
            </Col>
          </Row>
        )}
      </Card>

      {/* Items Summary */}
      <Card title="Items Summary" style={{ marginBottom: 24 }}>
        <Table
          dataSource={formData.items}
          columns={itemColumns}
          rowKey="id"
          pagination={false}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong>{formData.currency || 'ETB'} {formData.totalAmount?.toLocaleString()}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>

      {/* Attachments Summary */}
      {formData.attachments && formData.attachments.length > 0 && (
        <Card title="Attachments Summary" style={{ marginBottom: 24 }}>
          <Space wrap>
            {formData.attachments.map((att) => (
              <div key={att.fileId} style={{ border: "1px solid #eee", padding: 12, borderRadius: 6, minWidth: 200 }}>
                <Space direction="vertical" size="small">
                  <Space>
                    {getMimeTypeIcon(att.mimeType)}
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontWeight: 500, color: '#1890ff', textDecoration: 'none' }}
                      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                    >
                      {att.originalName}
                    </a>
                  </Space>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {formatBytes(att.size)}
                  </div>
                  <Tag color="blue" style={{ fontSize: '11px' }}>
                    {getCategoryLabel(att.category || 'OTHER')}
                  </Tag>
                </Space>
              </div>
            ))}
          </Space>
        </Card>
      )}

      {/* Approval Configuration */}
      <Card title="Approval Configuration" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Approval Type" required>
              <Radio.Group
                value={approvalType}
                onChange={(e) => setApprovalType(e.target.value)}
              >
                <Radio value="LOCAL">Local Approval</Radio>
                <Radio value="EXTERNAL">External Approval Flow</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {approvalType === 'EXTERNAL' && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Select Workflow" required>
                <Select
                  value={selectedWorkflow?._id}
                  onChange={(value) => setSelectedWorkflow(workflows.find(w => w._id === value) || selectedWorkflow)}
                  options={[
                    ...(selectedWorkflow && !workflows.find(w => w._id === selectedWorkflow._id) ? [{ value: selectedWorkflow._id, label: selectedWorkflow.name }] : []),
                    ...workflows.map(w => ({ value: w._id, label: w.name }))
                  ]}
                  placeholder="Choose a flow"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {selectedWorkflow && (
          <Row>
            <Col span={24}>
              <WorkflowDiagram flow={selectedWorkflow} />
            </Col>
          </Row>
        )}

        {approvalType === 'LOCAL' && (
          <Card
            title="Approval Steps"
            extra={
              <Space>
                <Button icon={<SortAscendingOutlined />} onClick={sortByOrder}>
                  Sort by Order
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={addStep}>
                  Add Step
                </Button>
              </Space>
            }
          >
            <Table
              dataSource={steps}
              columns={columns}
              rowKey={(_r, idx) => String(idx)}
              pagination={false}
              locale={{ emptyText: "No steps added yet" }}
              footer={() => (
                <div>
                  {steps?.length ? (
                    <Space wrap>
                      {steps.map((s) => (
                        <Tag key={`${s.roleSlug}-${s.order}`} color="blue">
                          {s.order}: {s.stepName || s.roleSlug}
                        </Tag>
                      ))}
                    </Space>
                  ) : (
                    <span style={{ color: "#888" }}>Add steps to define the serial approval flow.</span>
                  )}
                </div>
              )}
            />
          </Card>
        )}
      </Card>

      {/* Modal for adding/editing steps */}
      <Modal
        title={editingStepIndex !== null ? "Edit Approval Step" : "Add Approval Step"}
        visible={isStepModalVisible}
        onOk={saveStep}
        onCancel={() => {
          setIsStepModalVisible(false);
          stepForm.resetFields();
          setEditingStepIndex(null);
        }}
        okText={editingStepIndex !== null ? "Update Step" : "Add Step"}
        width={600}
      >
        <Form form={stepForm} layout="vertical">
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="roleSlug"
                label="Role Slug"
                rules={[{ required: true, message: 'Please enter role slug' }]}
              >
                <Input placeholder="e.g. TEAM_LEAD, FINANCE_MANAGER" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="stepName"
                label="Step Name"
              >
                <Input placeholder="Optional display name" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};